import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useGetBudgetConfigQuery,
  useUpdateBudgetConfigMutation,
  useGetAlertConfigQuery,
  useUpdateAlertConfigMutation,
} from "../../app/api/console/consoleTeamUsageApiSlice";

const ALERT_TYPE_LABELS = {
  per_person_daily: "Per-person daily",
  per_person_weekly: "Per-person weekly",
  team_weekly: "Team weekly",
  spike: "Spike (burst)",
};

const InfoTooltip = ({ text }) => (
  <span className='relative group'>
    <svg className='w-4 h-4 text-text-tertiary hover:text-text-secondary cursor-help inline-block ml-1' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
      <path strokeLinecap='round' strokeLinejoin='round' d='M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z' />
    </svg>
    <span className='invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-caption text-white bg-gray-900 rounded-lg shadow-lg w-64 z-50 text-center'>
      {text}
    </span>
  </span>
);

const SaveButton = ({ dirty, isSaving, onSave, status }) => (
  <div className='flex items-center gap-2'>
    {status === "saved" && <span className='text-body-xs text-green-600 font-medium'>Saved</span>}
    {status === "error" && <span className='text-body-xs text-red-600 font-medium'>Failed</span>}
    <button
      onClick={onSave}
      disabled={!dirty || isSaving}
      className={`px-4 py-2 text-body-xs font-medium rounded-lg transition-all ${
        dirty ? "bg-accent-purple text-white hover:bg-accent-purple/90" : "bg-surface-neutral text-text-tertiary cursor-not-allowed"
      }`}
    >
      {isSaving ? "Saving..." : "Save"}
    </button>
  </div>
);

const Toggle = ({ enabled, onChange, label }) => (
  <div className='flex items-center gap-3'>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent-purple focus:ring-offset-2 ${
        enabled ? "bg-accent-purple" : "bg-gray-200"
      }`}
      role='switch'
      aria-checked={enabled}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? "translate-x-5" : "translate-x-0"}`} />
    </button>
    <span className={`text-body-xs font-medium ${enabled ? "text-accent-purple" : "text-text-tertiary"}`}>{label}</span>
  </div>
);

const LimitInput = ({ value, onChange, placeholder = "—", width = "w-20" }) => (
  <div className='flex items-center gap-1'>
    <span className='text-body-sm text-text-tertiary'>$</span>
    <input
      type='number'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${width} px-2 py-1 text-body-xs rounded-md border border-border-subtle bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple tabular-nums`}
    />
  </div>
);

const SectionHeader = ({ icon, title, tooltip, badge, right }) => (
  <div className='flex items-center justify-between mb-4'>
    <div className='flex items-center gap-2'>
      {icon}
      <h3 className='text-body-lg font-semibold text-text-primary'>{title}</h3>
      {badge}
      {tooltip && <InfoTooltip text={tooltip} />}
    </div>
    {right}
  </div>
);

const AlertsSection = ({ env }) => {
  // currentData = config for THIS env only. Sticky `data` after an env toggle
  // keeps staging thresholds editable under the Production label (and Save
  // PUTs them to the new env).
  const {
    currentData: alertConfig,
    isLoading,
    isFetching,
  } = useGetAlertConfigQuery({ env });
  const configReady = Boolean(alertConfig);
  const [updateAlerts, { isLoading: isSaving }] = useUpdateAlertConfigMutation();
  const [alerts, setAlerts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    setDirty(false);
    setSaveStatus(null);
    if (!alertConfig?.alerts) {
      setAlerts([]);
      return;
    }
    setAlerts(alertConfig.alerts.map((a, i) => ({ ...a, _id: i })));
  }, [env, alertConfig]);

  const updateAlert = (idx, field, value) => {
    if (!configReady) return;
    setAlerts((prev) => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
    setDirty(true);
  };

  const removeAlert = (idx) => {
    if (!configReady) return;
    setAlerts((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addAlert = () => {
    if (!configReady) return;
    setAlerts((prev) => [...prev, { type: "per_person_daily", threshold_usd: 50, action: "warn", _id: Date.now() }]);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!configReady) return;
    setSaveStatus(null);
    try {
      await updateAlerts({
        env,
        alerts: alerts.map(({ _id, ...rest }) => rest),
        per_caller_overrides: alertConfig?.per_caller_overrides || {},
      }).unwrap();
      setSaveStatus("saved");
      setDirty(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch { setSaveStatus("error"); }
  };

  if (isLoading || (!configReady && isFetching)) {
    return <div className='py-4 text-text-tertiary text-body-xs'>Loading alert config...</div>;
  }
  if (!configReady) {
    return <div className='py-4 text-text-tertiary text-body-xs'>Alert config unavailable for this environment.</div>;
  }

  return (
    <div className='space-y-4'>
      <SectionHeader
        icon={<svg className='w-5 h-5 text-amber-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}><path strokeLinecap='round' strokeLinejoin='round' d='M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' /></svg>}
        title='Slack Alerts'
        badge={<span className='text-caption font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700'>Soft limit</span>}
        tooltip='When spend crosses a threshold, a Slack notification is sent to #zenyt-costs. No LLM calls are blocked.'
        right={
          <SaveButton
            dirty={configReady && dirty}
            isSaving={isSaving}
            onSave={handleSave}
            status={saveStatus}
          />
        }
      />

      <div className='border border-border-subtle rounded-xl overflow-hidden'>
        <table className='w-full'>
          <thead>
            <tr className='bg-surface-neutral/50'>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Type</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Threshold</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Action</th>
              <th className='text-right px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider w-16'></th>
            </tr>
          </thead>
          <tbody className='divide-y divide-border-subtle'>
            {alerts.map((alert, idx) => (
              <tr key={alert._id} className='hover:bg-surface-neutral/30'>
                <td className='px-4 py-2.5'>
                  <select
                    value={alert.type}
                    onChange={(e) => updateAlert(idx, "type", e.target.value)}
                    className='text-body-xs rounded-md border border-border-subtle bg-white px-2 py-1 text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple'
                  >
                    {Object.entries(ALERT_TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </td>
                <td className='px-4 py-2.5'>
                  <LimitInput value={alert.threshold_usd} onChange={(v) => updateAlert(idx, "threshold_usd", parseFloat(v) || 0)} />
                </td>
                <td className='px-4 py-2.5'>
                  <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-caption font-medium'>
                    <svg className='w-3 h-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}><path strokeLinecap='round' strokeLinejoin='round' d='M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' /></svg>
                    Slack alert
                  </span>
                </td>
                <td className='px-4 py-2.5 text-right'>
                  <button onClick={() => removeAlert(idx)} className='text-text-tertiary hover:text-red-500 transition-colors'>
                    <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}><path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' /></svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={addAlert} className='text-body-xs text-accent-purple font-medium hover:underline'>+ Add threshold</button>
    </div>
  );
};

const EnforcementSection = ({ env, usageData }) => {
  // currentData = budget config for THIS env only (see AlertsSection).
  const {
    currentData: config,
    isLoading,
    isFetching,
  } = useGetBudgetConfigQuery({ env });
  const configReady = Boolean(config);
  const [updateConfig, { isLoading: isSaving }] = useUpdateBudgetConfigMutation();

  const knownCallers = useMemo(() => {
    if (!configReady) return [];
    if (config?.known_callers?.length) return config.known_callers;
    if (usageData?.known_callers?.length) return usageData.known_callers;
    const fromUsage = (usageData?.callers || []).map((c) => c.caller).filter(Boolean);
    const fromConfig = (config?.caller_limits || []).map((c) => c.caller).filter(Boolean);
    return [...new Set([...fromUsage, ...fromConfig])].sort();
  }, [configReady, config, usageData]);

  const [enabled, setEnabled] = useState(false);
  const [envLimits, setEnvLimits] = useState({ daily: "", weekly: "", monthly: "" });
  const [callerLimits, setCallerLimits] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    setDirty(false);
    setSaveStatus(null);
    if (!config) {
      setEnabled(false);
      setEnvLimits({ daily: "", weekly: "", monthly: "" });
      setCallerLimits({});
      return;
    }
    setEnabled(config.enforcement_enabled || false);
    setEnvLimits({
      daily: config.env_daily_limit_usd != null ? String(config.env_daily_limit_usd) : "",
      weekly: config.env_weekly_limit_usd != null ? String(config.env_weekly_limit_usd) : "",
      monthly: config.env_monthly_limit_usd != null ? String(config.env_monthly_limit_usd) : "",
    });
    const limits = {};
    for (const cl of config.caller_limits || []) {
      if (cl.caller) {
        limits[cl.caller] = {
          daily: cl.daily_limit_usd != null ? String(cl.daily_limit_usd) : "",
          weekly: cl.weekly_limit_usd != null ? String(cl.weekly_limit_usd) : "",
          monthly: cl.monthly_limit_usd != null ? String(cl.monthly_limit_usd) : "",
        };
      }
    }
    setCallerLimits(limits);
  }, [env, config]);

  const setEnvField = (field, value) => {
    if (!configReady) return;
    setEnvLimits((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const setCallerField = useCallback((caller, field, value) => {
    setCallerLimits((prev) => ({
      ...prev,
      [caller]: { ...(prev[caller] || {}), [field]: value },
    }));
    setDirty(true);
  }, []);
  // edits gated by configReady at the inputs / SaveButton level

  const handleSave = async () => {
    if (!configReady) return;
    setSaveStatus(null);
    const body = {
      env,
      enforcement_enabled: enabled,
      env_monthly_limit_usd: envLimits.monthly ? parseFloat(envLimits.monthly) : null,
      env_daily_limit_usd: envLimits.daily ? parseFloat(envLimits.daily) : null,
      env_weekly_limit_usd: envLimits.weekly ? parseFloat(envLimits.weekly) : null,
      caller_limits: knownCallers.map((caller) => {
        const cl = callerLimits[caller] || {};
        return {
          caller,
          monthly_limit_usd: cl.monthly ? parseFloat(cl.monthly) : null,
          daily_limit_usd: cl.daily ? parseFloat(cl.daily) : null,
          weekly_limit_usd: cl.weekly ? parseFloat(cl.weekly) : null,
        };
      }),
    };
    try {
      await updateConfig(body).unwrap();
      setSaveStatus("saved");
      setDirty(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch { setSaveStatus("error"); }
  };

  const getCallerSpend = (caller) => {
    if (!usageData?.callers) return 0;
    return usageData.callers.find((x) => x.caller === caller)?.total_cost_usd || 0;
  };

  if (isLoading || (!configReady && isFetching)) {
    return <div className='py-4 text-text-tertiary text-body-xs'>Loading budget config...</div>;
  }
  if (!configReady) {
    return <div className='py-4 text-text-tertiary text-body-xs'>Budget config unavailable for this environment.</div>;
  }

  return (
    <div className='space-y-4'>
      <SectionHeader
        icon={<svg className='w-5 h-5 text-red-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}><path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z' /></svg>}
        title='Budget Enforcement'
        badge={<span className='text-caption font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700'>Hard limit</span>}
        tooltip='When enabled, LLM calls that would exceed the limit are blocked immediately with BudgetExceededError. Active scans will fail on their next LLM call.'
        right={
          <div className='flex items-center gap-4'>
            <Toggle
              enabled={enabled}
              onChange={(v) => {
                if (!configReady) return;
                setEnabled(v);
                setDirty(true);
              }}
              label={enabled ? "Active" : "Disabled"}
            />
            <SaveButton
              dirty={configReady && dirty}
              isSaving={isSaving}
              onSave={handleSave}
              status={saveStatus}
            />
          </div>
        }
      />

      {enabled && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-body-xs text-amber-800'>
          <strong>Warning:</strong> Enforcement is active. LLM calls will be blocked when limits are exceeded. Active scans may fail mid-execution.
        </div>
      )}

      <div className='flex items-center gap-6 flex-wrap'>
        <div className='flex items-center gap-2'>
          <label className='text-body-xs font-medium text-text-secondary w-24'>Env daily</label>
          <LimitInput value={envLimits.daily} onChange={(v) => setEnvField("daily", v)} />
        </div>
        <div className='flex items-center gap-2'>
          <label className='text-body-xs font-medium text-text-secondary w-24'>Env weekly</label>
          <LimitInput value={envLimits.weekly} onChange={(v) => setEnvField("weekly", v)} />
        </div>
        <div className='flex items-center gap-2'>
          <label className='text-body-xs font-medium text-text-secondary w-24'>Env monthly</label>
          <LimitInput value={envLimits.monthly} onChange={(v) => setEnvField("monthly", v)} />
        </div>
      </div>

      <div className='border border-border-subtle rounded-xl overflow-hidden'>
        <table className='w-full'>
          <thead>
            <tr className='bg-surface-neutral/50'>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Person</th>
              <th className='text-right px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Spend</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Daily</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Weekly</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>Monthly</th>
              <th className='text-left px-4 py-2.5 text-body-xs font-medium text-text-secondary uppercase tracking-wider w-[140px]'>Progress</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-border-subtle'>
            {knownCallers.map((caller) => {
              const spend = getCallerSpend(caller);
              const cl = callerLimits[caller] || {};
              const monthlyLimit = cl.monthly ? parseFloat(cl.monthly) : null;
              const pct = monthlyLimit ? (spend / monthlyLimit) * 100 : null;
              const isOver = pct != null && pct >= 100;
              const isWarning = pct != null && pct >= 80 && pct < 100;

              return (
                <tr key={caller} className='hover:bg-surface-neutral/30 transition-colors'>
                  <td className='px-4 py-2.5'>
                    <span className='text-body-sm font-medium text-text-primary'>{caller}</span>
                  </td>
                  <td className='px-4 py-2.5 text-right'>
                    <span className='text-body-xs text-text-secondary tabular-nums'>${spend.toFixed(2)}</span>
                  </td>
                  <td className='px-4 py-2.5'>
                    <LimitInput value={cl.daily || ""} onChange={(v) => setCallerField(caller, "daily", v)} />
                  </td>
                  <td className='px-4 py-2.5'>
                    <LimitInput value={cl.weekly || ""} onChange={(v) => setCallerField(caller, "weekly", v)} />
                  </td>
                  <td className='px-4 py-2.5'>
                    <LimitInput value={cl.monthly || ""} onChange={(v) => setCallerField(caller, "monthly", v)} />
                  </td>
                  <td className='px-4 py-2.5'>
                    {monthlyLimit != null ? (
                      <div className='flex items-center gap-2'>
                        <div className='flex-1 h-1.5 bg-surface-neutral rounded-full overflow-hidden'>
                          <div
                            className={`h-full rounded-full transition-all ${isOver ? "bg-red-500" : isWarning ? "bg-amber-500" : "bg-accent-purple"}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className={`text-caption tabular-nums ${isOver ? "text-red-600" : isWarning ? "text-amber-600" : "text-text-tertiary"}`}>
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    ) : (
                      <span className='text-caption text-text-tertiary'>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BudgetConfigPanel = ({ env, usageData }) => {
  return (
    <div className='space-y-8'>
      <AlertsSection env={env} />
      <div className='border-t border-border-subtle' />
      <EnforcementSection env={env} usageData={usageData} />
    </div>
  );
};

export default BudgetConfigPanel;
